import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OWNER_EMAIL, WHATSAPP_DISPLAY } from "../config";
import { useLang } from "../LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.errFill);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thank you!");
      setForm(initial);
    } catch (err) {
      toast.error(t.contact.errFail);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-white placeholder:text-[#666] transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact" className="relative py-24 md:py-32 bg-[#050505] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#d4af37]/8 blur-[120px] z-0" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow testId="contact-eyebrow">{t.contact.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
              {t.contact.title1} <span className="text-gold-gradient italic">{t.contact.title2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#a3a3a3] text-base md:text-lg leading-relaxed max-w-md">{t.contact.subtitle}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-5">
              <a href={`mailto:${OWNER_EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-4 text-[#d4d4d4] hover:text-[#d4af37] transition-colors duration-300">
                <span className="w-11 h-11 rounded-full glass flex items-center justify-center text-[#d4af37]"><Mail size={18} /></span>
                {OWNER_EMAIL}
              </a>
              <a href="tel:+12408176538" data-testid="contact-phone-link" className="flex items-center gap-4 text-[#d4d4d4] hover:text-[#d4af37] transition-colors duration-300">
                <span className="w-11 h-11 rounded-full glass flex items-center justify-center text-[#d4af37]"><Phone size={18} /></span>
                {WHATSAPP_DISPLAY}
              </a>
              <div className="flex items-center gap-4 text-[#d4d4d4]">
                <span className="w-11 h-11 rounded-full glass flex items-center justify-center text-[#d4af37]"><MapPin size={18} /></span>
                {t.contact.location}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="glass rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-wide uppercase text-[#888]">{t.contact.name}</label>
                <input data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs tracking-wide uppercase text-[#888]">{t.contact.email}</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs tracking-wide uppercase text-[#888]">{t.contact.phone}</label>
                <input data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs tracking-wide uppercase text-[#888] block mb-1">{t.contact.service}</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="contact-service" className="w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 h-auto text-white focus:ring-0 focus:border-[#d4af37] data-[placeholder]:text-[#666]">
                    <SelectValue placeholder={t.contact.selectService} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10 text-white">
                    {t.services.items.map((s, i) => (
                      <SelectItem key={i} value={s.title} className="focus:bg-[#d4af37]/15 focus:text-white">{s.title}</SelectItem>
                    ))}
                    <SelectItem value={t.contact.general} className="focus:bg-[#d4af37]/15 focus:text-white">{t.contact.general}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs tracking-wide uppercase text-[#888]">{t.contact.message}</label>
              <textarea data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} placeholder={t.contact.messagePh} className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group mt-9 w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full px-9 py-4 bg-[#d4af37] text-[#050505] font-medium hover:bg-[#e9d08a] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> {t.contact.sending}</> : <>{t.contact.send} <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
